/**
 * Core cryptographic primitives for end-to-end encryption.
 *
 * Uses Web Crypto API for all operations where available, and @noble/hashes for Argon2id.
 *
 * @module crypto
 */

import { argon2id } from "@noble/hashes/argon2.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { randomBytes } from "@noble/hashes/utils.js";
import { z } from "zod";

/**
 * Argon2id parameters for key derivation.
 * These parameters provide strong security against brute force and side-channel attacks.
 */
const ARGON2_PARAMS = {
  /** Time cost parameter (t) - number of iterations */
  t: 3,
  /** Memory cost parameter (m) - memory in KiB */
  m: 65536,
  /** Parallelism parameter (p) - number of threads */
  p: 4,
  /** Output hash length in bytes (dkLen) */
  dkLen: 32,
} as const;

/**
 * Key sizes in bytes for various cryptographic operations.
 */
const KEY_SIZES = {
  /** AES-256-GCM key size */
  aes256: 32,
  /** X25519 private key size */
  x25519Private: 32,
  /** X25519 public key size */
  x25519Public: 32,
  /** Recovery code hash length */
  recoveryCodeHash: 32,
  /** Nonce/IV length for AES-GCM */
  nonce: 12,
} as const;

/**
 * Key type labels for Web Crypto API import/export.
 */
const KEY_ALGORITHMS = {
  /** AES-256-GCM */
  aes256Gcm: { name: "AES-GCM", length: 256 } as const,
  /** ECDH P-256 */
  ecdhP256: { name: "ECDH", namedCurve: "P-256" } as const,
  /** HMAC-SHA-256 */
  hmacSha256: { name: "HMAC", hash: "SHA-256" } as const,
} as const;

/**
 * Represents a public/private key pair for asymmetric encryption.
 */
export interface KeyPair {
  /** The private key for decryption */
  privateKey: CryptoKey;
  /** The public key for encryption */
  publicKey: CryptoKey;
}

/** Zod schema for wrapped private key (validates JSON from server). */
export const WrappedPrivateKeySchema = z.object({
  ciphertext: z.string(),
  iv: z.string(),
  tag: z.string(),
});

/**
 * Represents a wrapped (encrypted) private key.
 */
export type WrappedPrivateKey = z.infer<typeof WrappedPrivateKeySchema>;

/**
 * Represents encrypted data using AES-256-GCM.
 */
export interface EncryptedData {
  /** The encrypted data (base64url encoded) */
  ciphertext: string;
  /** The initialization vector (base64url encoded) */
  iv: string;
  /** Authentication tag for AES-GCM (base64url encoded) */
  tag: string;
}

/**
 * Represents a wrapped (encrypted) data encryption key (DEK).
 */
export interface WrappedDek {
  /** The encrypted DEK (base64url encoded) */
  ciphertext: string;
  /** The initialization vector (base64url encoded) */
  iv: string;
  /** Authentication tag for AES-GCM (base64url encoded) */
  tag: string;
  /** The ephemeral public key used for ECDH (base64url encoded) */
  ephemeralPublicKey: string;
}

/**
 * Represents a recovery code and its hashed value.
 */
export interface RecoveryCodeSet {
  /** The 10 recovery codes for display to the user */
  codes: readonly string[];
  /** The hashed codes for storage and verification */
  hashedCodes: readonly string[];
}

/**
 * Converts an ArrayBuffer to a base64url string (URL-safe, no padding).
 *
 * @param buffer - The buffer to encode
 * @returns The base64url encoded string
 */
export function bufferToBase64Url(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Converts a base64url string to an ArrayBuffer.
 *
 * @param encoded - The base64url encoded string
 * @returns The decoded buffer
 */
export function base64UrlToBuffer(encoded: string): ArrayBuffer {
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Generates an X25519 key pair for asymmetric encryption.
 *
 * Note: Web Crypto API doesn't support X25519 directly in all environments.
 * We use ECDH P-256 as a widely-supported alternative that still provides
 * strong security (128-bit security level).
 *
 * @returns A promise resolving to a key pair with private and public keys
 */
export async function generateKeyPair(): Promise<KeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    ["deriveKey", "deriveBits"],
  );

  return {
    privateKey: keyPair.privateKey,
    publicKey: keyPair.publicKey,
  };
}

/**
 * Derives a Key Encryption Key (KEK) from a PIN and salt using Argon2id.
 *
 * The salt should be random and unique per user. The KEK is used to wrap/unwrap
 * the user's private key.
 *
 * @param pin - The user's PIN (as string, will be UTF-8 encoded)
 * @param salt - A random salt (hex string)
 * @returns A Promise resolving to the derived 256-bit key
 */
export async function deriveKEK(pin: string, salt: string): Promise<CryptoKey> {
  const pinBytes = new TextEncoder().encode(pin);
  const saltBytes = new Uint8Array(
    salt.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)),
  );

  const derivedKey = argon2id(pinBytes, saltBytes, ARGON2_PARAMS);

  return crypto.subtle.importKey(
    "raw",
    new Uint8Array(derivedKey),
    KEY_ALGORITHMS.aes256Gcm,
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Wraps (encrypts) a private key using a Key Encryption Key (KEK).
 *
 * @param privateKey - The private key to wrap
 * @param kek - The Key Encryption Key derived from the PIN
 * @returns A Promise resolving to the wrapped private key
 */
export async function wrapPrivateKey(
  privateKey: CryptoKey,
  kek: CryptoKey,
): Promise<WrappedPrivateKey> {
  const iv = crypto.getRandomValues(new Uint8Array(KEY_SIZES.nonce));
  const exportedKey = await crypto.subtle.exportKey("pkcs8", privateKey);
  const keyBytes = new Uint8Array(exportedKey);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    kek,
    keyBytes,
  );

  const ciphertext = new Uint8Array(encrypted.slice(0, -16));
  const tag = new Uint8Array(encrypted.slice(-16));

  return {
    ciphertext: bufferToBase64Url(ciphertext.buffer),
    iv: bufferToBase64Url(iv.buffer),
    tag: bufferToBase64Url(tag.buffer),
  };
}

/**
 * Unwraps (decrypts) a wrapped private key using a Key Encryption Key (KEK).
 *
 * @param wrappedKey - The wrapped private key
 * @param kek - The Key Encryption Key derived from the PIN
 * @returns A Promise resolving to the decrypted private key
 */
export async function unwrapPrivateKey(
  wrappedKey: WrappedPrivateKey,
  kek: CryptoKey,
): Promise<CryptoKey> {
  const iv = base64UrlToBuffer(wrappedKey.iv);
  const ciphertext = base64UrlToBuffer(wrappedKey.ciphertext);
  const tag = base64UrlToBuffer(wrappedKey.tag);

  // Combine ciphertext and tag for AES-GCM decryption
  const combined = new Uint8Array(ciphertext.byteLength + tag.byteLength);
  combined.set(new Uint8Array(ciphertext), 0);
  combined.set(new Uint8Array(tag), ciphertext.byteLength);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    kek,
    combined,
  );

  return crypto.subtle.importKey(
    "pkcs8",
    decrypted,
    KEY_ALGORITHMS.ecdhP256,
    true,
    ["deriveKey", "deriveBits"],
  );
}

/**
 * Encrypts data using AES-256-GCM with the provided key.
 *
 * @param data - The data to encrypt (string will be UTF-8 encoded)
 * @param key - The AES-256-GCM key
 * @returns A Promise resolving to the encrypted data
 */
export async function encryptData(
  data: string | Uint8Array,
  key: CryptoKey,
): Promise<EncryptedData> {
  const iv = crypto.getRandomValues(new Uint8Array(KEY_SIZES.nonce));
  const dataBytes =
    typeof data === "string" ? new TextEncoder().encode(data) : data;

  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    new Uint8Array(dataBytes),
  );

  const ciphertext = new Uint8Array(encrypted.slice(0, -16));
  const tag = new Uint8Array(encrypted.slice(-16));

  return {
    ciphertext: bufferToBase64Url(ciphertext.buffer),
    iv: bufferToBase64Url(iv.buffer),
    tag: bufferToBase64Url(tag.buffer),
  };
}

/**
 * Decrypts data that was encrypted using AES-256-GCM.
 *
 * @param encryptedData - The encrypted data
 * @param key - The AES-256-GCM key used for encryption
 * @returns A Promise resolving to the decrypted data as a Uint8Array
 */
export async function decryptData(
  encryptedData: EncryptedData,
  key: CryptoKey,
): Promise<Uint8Array> {
  const iv = base64UrlToBuffer(encryptedData.iv);
  const ciphertext = base64UrlToBuffer(encryptedData.ciphertext);
  const tag = base64UrlToBuffer(encryptedData.tag);

  // Combine ciphertext and tag for AES-GCM decryption
  const combined = new Uint8Array(ciphertext.byteLength + tag.byteLength);
  combined.set(new Uint8Array(ciphertext), 0);
  combined.set(new Uint8Array(tag), ciphertext.byteLength);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    combined,
  );

  return new Uint8Array(decrypted);
}

/**
 * Generates a random Data Encryption Key (DEK) for AES-256-GCM.
 *
 * DEKs are used for actual data encryption and can be wrapped with
 * different keys for multiple recipients.
 *
 * @returns A Promise resolving to the generated DEK
 */
export async function generateDek(): Promise<CryptoKey> {
  const keyBytes = crypto.getRandomValues(new Uint8Array(KEY_SIZES.aes256));
  return crypto.subtle.importKey(
    "raw",
    keyBytes.buffer,
    KEY_ALGORITHMS.aes256Gcm,
    true,
    ["encrypt", "decrypt"],
  );
}

/**
 * Wraps a Data Encryption Key (DEK) using a public key for a recipient.
 *
 * This uses ECDH to derive a shared secret, which is then used to wrap the DEK.
 * The ephemeral public key is included in the wrapped DEK so the recipient can
 * derive the same shared secret.
 *
 * @param dek - The Data Encryption Key to wrap
 * @param recipientPublicKey - The recipient's public key
 * @returns A Promise resolving to the wrapped DEK
 */
export async function wrapDek(
  dek: CryptoKey,
  recipientPublicKey: CryptoKey,
): Promise<WrappedDek> {
  // Generate an ephemeral key pair for this wrapping operation
  const ephemeralKeyPair = await generateKeyPair();

  // Derive a shared secret using ECDH with the recipient's public key
  const sharedSecretBits = await crypto.subtle.deriveBits(
    {
      name: "ECDH",
      public: recipientPublicKey,
    },
    ephemeralKeyPair.privateKey,
    256,
  );

  // Derive an AES key from the shared secret using HKDF
  const baseKey = await crypto.subtle.importKey(
    "raw",
    sharedSecretBits,
    "HKDF",
    false,
    ["deriveKey"],
  );

  const wrappingAesKey = await crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: new TextEncoder().encode("DEK wrapping"),
    },
    baseKey,
    KEY_ALGORITHMS.aes256Gcm,
    false,
    ["encrypt"],
  );

  const iv = crypto.getRandomValues(new Uint8Array(KEY_SIZES.nonce));
  const exportedDek = await crypto.subtle.exportKey("raw", dek);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    wrappingAesKey,
    exportedDek,
  );

  const ciphertext = new Uint8Array(encrypted.slice(0, -16));
  const tag = new Uint8Array(encrypted.slice(-16));

  // Export ephemeral public key for recipient to derive the same shared secret
  const ephemeralPublicKeyBytes = await crypto.subtle.exportKey(
    "raw",
    ephemeralKeyPair.publicKey,
  );

  return {
    ciphertext: bufferToBase64Url(ciphertext.buffer),
    iv: bufferToBase64Url(iv.buffer),
    tag: bufferToBase64Url(tag.buffer),
    ephemeralPublicKey: bufferToBase64Url(ephemeralPublicKeyBytes),
  };
}

/**
 * Unwraps a Data Encryption Key (DEK) using a private key.
 *
 * This reverses the wrapDek operation to recover the DEK.
 *
 * @param wrappedDek - The wrapped DEK
 * @param ephemeralPublicKey - The ephemeral public key from the wrapped DEK
 * @param privateKey - The recipient's private key
 * @returns A Promise resolving to the unwrapped DEK
 */
export async function unwrapDek(
  wrappedDek: WrappedDek,
  ephemeralPublicKey: CryptoKey,
  privateKey: CryptoKey,
): Promise<CryptoKey> {
  // Derive the same shared secret using the private key and ephemeral public key
  const sharedSecretBits = await crypto.subtle.deriveBits(
    {
      name: "ECDH",
      public: ephemeralPublicKey,
    },
    privateKey,
    256,
  );

  // Derive the same AES wrapping key using HKDF
  const baseKey = await crypto.subtle.importKey(
    "raw",
    sharedSecretBits,
    "HKDF",
    false,
    ["deriveKey"],
  );

  const wrappingAesKey = await crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: new TextEncoder().encode("DEK wrapping"),
    },
    baseKey,
    KEY_ALGORITHMS.aes256Gcm,
    false,
    ["decrypt"],
  );

  const iv = base64UrlToBuffer(wrappedDek.iv);
  const ciphertext = base64UrlToBuffer(wrappedDek.ciphertext);
  const tag = base64UrlToBuffer(wrappedDek.tag);

  const combined = new Uint8Array(ciphertext.byteLength + tag.byteLength);
  combined.set(new Uint8Array(ciphertext), 0);
  combined.set(new Uint8Array(tag), ciphertext.byteLength);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    wrappingAesKey,
    combined,
  );

  return crypto.subtle.importKey(
    "raw",
    decrypted,
    KEY_ALGORITHMS.aes256Gcm,
    true,
    ["encrypt", "decrypt"],
  );
}

/**
 * Generates 10 recovery codes for account recovery.
 *
 * Recovery codes are one-time use codes that can be used to restore access
 * to encrypted data. They are displayed to the user during setup.
 *
 * @returns A Promise resolving to the recovery code set with codes and hashes
 */
export async function generateRecoveryCodes(): Promise<RecoveryCodeSet> {
  const codes: string[] = [];
  const hashedCodes: string[] = [];

  for (let i = 0; i < 10; i++) {
    // Generate 6 random bytes (12 hex characters) for a 48-bit code
    const codeBytes = randomBytes(6);
    const code = Array.from(codeBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();

    // Format as XXXXX-XXXXX for readability
    const formattedCode = `${code.slice(0, 5)}-${code.slice(5)}`;
    codes.push(formattedCode);

    // Hash the code without formatting for storage
    const codeBuffer = new TextEncoder().encode(code);
    const hashed = sha256(codeBuffer);
    hashedCodes.push(bufferToBase64Url(hashed));
  }

  return {
    codes,
    hashedCodes,
  };
}

/**
 * Derives a recovery Key Encryption Key (KEK) from a recovery code using HKDF.
 *
 * Recovery codes have ~48 bits of entropy (6 random bytes), which is sufficient
 * for HKDF (no slow hash needed — unlike PINs, codes are machine-generated).
 *
 * @param code - The recovery code (formatted or unformatted)
 * @param salt - A random salt (Uint8Array, stored alongside the wrapped keys)
 * @returns A Promise resolving to the derived AES-256-GCM key
 */
export async function deriveRecoveryKEK(
  code: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const normalizedCode = code.replace(/-/g, "").toUpperCase();
  const codeBytes = new TextEncoder().encode(normalizedCode);

  const baseKey = await crypto.subtle.importKey(
    "raw",
    codeBytes,
    "HKDF",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: salt.buffer as ArrayBuffer,
      info: new TextEncoder().encode("recovery KEK"),
    },
    baseKey,
    KEY_ALGORITHMS.aes256Gcm,
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Wraps a private key for each recovery code independently.
 *
 * Produces one wrapped key per code, all sharing the same salt.
 * Any single code can later unwrap the private key.
 *
 * @param privateKey - The private key to wrap
 * @param recoveryCodes - The 10 plaintext recovery codes
 * @returns The wrapped keys (indexed by code position) and the shared salt
 */
export async function wrapPrivateKeyForRecovery(
  privateKey: CryptoKey,
  recoveryCodes: readonly string[],
): Promise<{ wrappedKeys: WrappedPrivateKey[]; recoverySalt: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltHex = Array.from(salt)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  const wrappedKeys = await Promise.all(
    recoveryCodes.map(async (code) => {
      const recoveryKEK = await deriveRecoveryKEK(code, salt);
      return wrapPrivateKey(privateKey, recoveryKEK);
    }),
  );

  return { wrappedKeys, recoverySalt: saltHex };
}

/**
 * Unwraps a private key using a recovery code.
 *
 * @param wrappedKey - The wrapped private key (from the matching code index)
 * @param code - The plaintext recovery code
 * @param recoverySalt - The hex-encoded salt used during wrapping
 * @returns A Promise resolving to the unwrapped private key
 */
export async function unwrapPrivateKeyFromRecovery(
  wrappedKey: WrappedPrivateKey,
  code: string,
  recoverySalt: string,
): Promise<CryptoKey> {
  const saltBytes = new Uint8Array(
    recoverySalt.match(/.{1,2}/g)!.map((b) => parseInt(b, 16)),
  );
  const recoveryKEK = await deriveRecoveryKEK(code, saltBytes);
  return unwrapPrivateKey(wrappedKey, recoveryKEK);
}

/**
 * Hashes a recovery code for verification.
 *
 * This allows verification without storing the actual codes.
 *
 * @param code - The recovery code to hash (formatted or unformatted)
 * @returns The hashed code as a base64url string
 */
export function hashRecoveryCode(code: string): string {
  // Remove any dashes from the input
  const normalizedCode = code.replace(/-/g, "").toUpperCase();
  const codeBuffer = new TextEncoder().encode(normalizedCode);
  const hashed = sha256(codeBuffer);
  return bufferToBase64Url(hashed);
}
